import { UPLOADS_URL } from "../../config/constants";
import {
  activitySlug,
  findActivityImageFilename,
  slugFromActivityFilename,
} from "../activities/activityHelpers";

const VIDEO_FILE = /\.(mp4|mov|m4v|webm|avi)(\?|$)/i;
const COVER_STILL = /video-thumb|post-image-thumb-|bw-stills|frame-/i;

export function isVideoFile(path = "") {
  return VIDEO_FILE.test(String(path));
}

export function isCoverStill(path = "") {
  return COVER_STILL.test(String(path));
}

/** Stream URL. The API serves videos with range requests, so the player never downloads the file first. */
export function uploadUrl(filename) {
  const raw = String(filename || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  const name = raw.split("/").pop().split("?")[0];
  return `${UPLOADS_URL}/${encodeURIComponent(name)}`;
}

/**
 * Same order as the apps: videos first, then photos.
 * A generated cover still sits behind the video and is not shown as its own photo.
 */
export function collectPostMedia(post) {
  const images = Array.isArray(post?.images) ? post.images.filter(Boolean) : [];
  const videos = Array.isArray(post?.videos) ? post.videos.filter(Boolean) : [];
  const imageVideos = images.filter((path) => isVideoFile(path) && !videos.includes(path));
  const allVideos = [...videos, ...imageVideos];
  const photos = images.filter((path) => !isVideoFile(path));
  const covers = photos.filter(isCoverStill);
  const photoList = allVideos.length ? photos.filter((path) => !isCoverStill(path)) : photos;

  let ordered;
  if (allVideos.length === 1 && photoList.length === 1 && photos.length === 1) {
    ordered = allVideos;
  } else if (allVideos.length && !photoList.length) {
    ordered = allVideos;
  } else {
    ordered = [...allVideos, ...photoList];
  }

  const posterFor = (videoIndex) => {
    if (covers[videoIndex]) return uploadUrl(covers[videoIndex]);
    if (covers[0] && videoIndex === 0) return uploadUrl(covers[0]);
    if (allVideos.length === 1 && photos.length === 1) return uploadUrl(photos[0]);
    return "";
  };

  let videoIndex = 0;
  return ordered.map((file, index) => {
    const video = isVideoFile(file);
    const item = {
      id: `${file}-${index}`,
      kind: video ? "video" : "image",
      src: uploadUrl(file),
      poster: video ? posterFor(videoIndex) : "",
    };
    if (video) videoIndex += 1;
    return item;
  });
}

export const POST_TYPES = [
  { value: "CHILD", label: "Child" },
  { value: "CLASS", label: "Class" },
];

export function postTypeLabel(type) {
  return POST_TYPES.find((item) => item.value === type)?.label || type || "—";
}

export function authorName(author) {
  if (!author) return "—";
  const name = `${author.firstName || ""} ${author.lastName || ""}`.trim();
  return name || "—";
}

export function commentAuthorName(author) {
  if (!author) return "Unknown";
  const teacher = `${author.firstName || ""} ${author.lastName || ""}`.trim();
  if (teacher) return teacher;
  const mother = `${author.motherFirstName || ""} ${author.motherLastName || ""}`.trim();
  if (mother) return mother;
  return "Unknown";
}

export function commentAuthorRole(author) {
  if (!author) return "";
  if (author.role === "teacher" || author.role === "parent") return author.role;
  if (author.firstName || author.lastName) return "teacher";
  if (author.motherFirstName || author.motherLastName) return "parent";
  return "";
}

export function authorInitials(author) {
  if (!author) return "?";
  const first = (author.firstName || "?").charAt(0);
  const last = (author.lastName || "").charAt(0);
  return `${first}${last}`.toUpperCase();
}

export function activityTitle(activity) {
  if (!activity) return "—";
  return activity.title || "—";
}

export function resolvePostActivity(post, activities = []) {
  if (!post) return null;

  const embedded = post.activity;
  if (embedded && typeof embedded === "object" && (embedded.title || embedded.image)) {
    return embedded;
  }

  const activityId = post.activityId || embedded?._id || embedded;
  if (activityId && activities.length) {
    const id = String(activityId);
    const match = activities.find((item) => String(item._id) === id);
    if (match) return match;
  }

  const seedImage = findActivityImageFilename(post.images);
  if (seedImage) {
    const slug = slugFromActivityFilename(seedImage);
    const filename = String(seedImage).split("/").pop();

    if (activities.length) {
      const match = activities.find((item) => activitySlug(item.title) === slug);
      if (match) return match;
    }

    const title = slug
      .split("-")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

    return { title, image: filename };
  }

  return null;
}

export function snippet(text = "", max = 100) {
  const value = feedContent(text);
  if (!value) return "—";
  return value.length > max ? `${value.slice(0, max).trim()}…` : value;
}

export function feedContent(text = "") {
  return String(text || "")
    .trim()
    .replace(/^\[Seed\]\s*/, "");
}

export function engagementSummary(post) {
  const likes = Array.isArray(post.likes) ? post.likes.length : 0;
  const comments = post.commentsCount ?? 0;
  return { likes, comments };
}

export function childName(child) {
  if (!child) return "—";
  const name = `${child.firstName || ""} ${child.lastName || ""}`.trim();
  return name || "—";
}

export function classroomName(classroom) {
  if (!classroom) return "—";
  return classroom.classroomName || classroom.name || classroom.className || "—";
}

export function targetsSummary(post) {
  if (post.type === "CLASS") {
    const room = Array.isArray(post.classroom) ? post.classroom[0] : post.classroom;
    return classroomName(room);
  }
  const kids = post.children || [];
  if (!kids.length) return "—";
  if (kids.length === 1) {
    const kid = kids[0];
    return typeof kid === "object" ? childName(kid) : "1 student";
  }
  return `${kids.length} students`;
}
